import type { ComponentType } from 'react';
import { cn } from '../../utils/utils';
import { useRovingRadioGroup } from '../../hooks/useRovingRadioGroup';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  /** Drawn before the label, decorative (the label names the segment). */
  icon?: ComponentType<{ className?: string }>;
}

export interface SegmentedControlProps<T extends string> {
  /**
   * The group's accessible name. Never rendered - applied as `aria-label`.
   * Required: an unnamed radiogroup announces as an anonymous group, which is
   * most of what this component exists to fix.
   */
  label: string;
  /** `readonly` because callers build their option lists with `as const`. */
  options: readonly SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /**
   * `md` is the page-header size; `sm` is the in-card size. One uniform step:
   * container radius, container padding, segment radius, segment padding and
   * the responsive type bump all shrink together.
   */
  size?: 'sm' | 'md';
  className?: string;
  /**
   * Classes for every segment. For what the container's `className` cannot
   * reach - chiefly the app's `tap-target`, a plain CSS class that adds an
   * invisible 44px tap area on phones, so a segment keeps its drawn size and
   * still takes a finger. (`[&>button]:` variants only reach Tailwind
   * utilities.)
   */
  segmentClassName?: string;
}

const SIZE_CLASSES = {
  sm: {
    container: 'rounded-md p-0.5',
    segment: 'rounded-sm px-2.5 py-1 text-xs',
  },
  md: {
    // `sm:text-sm` belongs to the SIZE, not to the call site: the header
    // control is 12px on phones and 14px from the `sm` breakpoint up, while
    // the in-card control stays 12px at every width - it already sits inside
    // a card with its own responsive padding.
    container: 'rounded-lg p-1',
    segment: 'rounded-md px-3 py-1.5 text-xs sm:text-sm',
  },
};

/**
 * A single-choice segmented control, wired as a real radiogroup.
 *
 * `role='radio'` rather than `aria-pressed`, for the reason `OptionTile` in
 * the app already documents: these are single-choice - picking one unpicks the
 * others - and `aria-pressed` models independent toggles, so a screen reader
 * announces "pressed" on several unrelated buttons and offers no way to move
 * between them. As radios they announce "selected, 2 of 3" and get arrow-key
 * navigation. The keyboard machinery here mirrors that component's
 * `TileRadioGroup` deliberately, so the two read as siblings.
 *
 * NOT `TabbedInterface`. That component's `Tab` requires a `content` node and
 * it renders the panel itself inside a `flex h-full flex-col` root - it is a
 * tab set plus panel that owns vertical layout. This renders no panel, is
 * `inline-flex`, and is a filter rather than a navigation surface.
 *
 * Selection follows focus, the APG default for a radiogroup with no expensive
 * side effects.
 */
export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
  size = 'md',
  className,
  segmentClassName,
}: SegmentedControlProps<T>) {
  // `select` and `selectedIndex` stay inside the hook: the click path goes
  // straight to `onChange` (a pointer user is already where they want to be, so
  // there is nothing to move focus to), and selection is read off `value`.
  const {
    items: segments,
    tabbableIndex,
    handleKeyDown,
  } = useRovingRadioGroup({ options, value, onChange });
  const sizeClass = SIZE_CLASSES[size];

  return (
    <div
      role='radiogroup'
      aria-label={label}
      className={cn(
        'border-border inline-flex border',
        sizeClass.container,
        className
      )}
    >
      {options.map((option, index) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            ref={el => {
              segments.current[index] = el;
            }}
            type='button'
            role='radio'
            aria-checked={active}
            tabIndex={index === tabbableIndex ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={event => handleKeyDown(event, index)}
            className={cn(
              'font-medium transition-all duration-200',
              // No call site had a focus ring, which becomes a 2.4.7 failure
              // the moment the control is keyboard-navigable. `ring-1` with no
              // offset, because a segment sits inside a `p-0.5`/`p-1`
              // container where an offset ring would clip.
              'focus-visible:ring-ring focus-visible:ring-1 focus-visible:outline-none',
              sizeClass.segment,
              segmentClassName,
              active
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {option.icon ? (
              <span className='inline-flex items-center gap-1'>
                <span aria-hidden='true' className='contents'>
                  <option.icon className='h-3.5 w-3.5' />
                </span>
                {option.label}
              </span>
            ) : (
              option.label
            )}
          </button>
        );
      })}
    </div>
  );
}
